import { capitalize, findIndex } from 'lodash';
import { FastField } from 'formik';
import {
  ListItemButton, ListItemIcon, ListItemText, RadioGroup as MuiRadioGroup,
  Radio as MuiRadio,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { fieldToRadioGroup } from 'formik-mui';
import type { FieldProps } from 'formik';
import type { RadioGroupProps } from '@mui/material';

const useStyles = makeStyles({ name: 'ToggleField' })((theme) => ({
  root: {
    backgroundColor: 'transparent !important',
  },
  listItemIcon: {
    minHeight: 28,
    minWidth: 28,
    padding: 3,
  },
  colorPrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.grey[50],
  },
  colorWarning: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.grey[50],
  },
  colorSuccess: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.grey[50],
  },
  radioGroup: {
    display: 'none',
  },
}));

interface ToggleState {
  readonly value: string | number;
  readonly color: string;
  readonly label: string;
  readonly short?: string;
}

interface ToggleFieldProps {
  readonly states: ToggleState[];
  readonly disabled?: boolean;
}

export const ToggleField: React.FC<ToggleFieldProps & RadioGroupProps> = ({
  states, name,
  disabled = false,
  ...restProps
}) => {
  const { classes, cx } = useStyles();

  return (
    <FastField
      disabled={disabled}
      name={name}
    >
      {(props: FieldProps<string>) => {
        const index = findIndex(states, (toggleState) => toggleState.value === props.field.value);
        const state = states[index];

        const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (states[index + 1]) {
            void props.form.setFieldValue(name, states[index + 1].value);
          } else {
            void props.form.setFieldValue(name, states[0].value);
          }
        };

        return (
          <ListItemButton
            className={classes.root}
            disabled={disabled}
            onClick={handleClick}
            disableGutters
          >
            <ListItemIcon
              className={cx(classes.listItemIcon, classes[`color${capitalize(state.color)}`])}
            >
              <SwapHorizIcon />
            </ListItemIcon>

            <ListItemText
              primary={state.label}
              primaryTypographyProps={{
                variant: 'subtitle1',
                color: 'textSecondary',
              }}
            />

            <MuiRadioGroup
              {...restProps}
              {...fieldToRadioGroup(props)}
              className={classes.radioGroup}
            >
              {states.map((item) => (
                <MuiRadio
                  key={item.value}
                  value={item.value}
                />
              ))}
            </MuiRadioGroup>
          </ListItemButton>
        );
      }}
    </FastField>
  );
};
